// public/js/features/tracker-config/battery.js
// Battery-life estimator for Digital Matter trackers. Pure function + a
// per-device-family coefficient table taken from Digital Matter's own public
// battery calculators (the .xlsx "Constants" tables). We reproduce DM's linear
// energy-budget model: life = usable_capacity / (mAh per day). See
// docs/battery-life.md for the source URLs and the exact coefficients.
//
// The GPS fix time and a "conditions" derate are genuine assumptions (DM gives
// ranges, not values). The fix time is NOT a user input - nobody can realistically
// know their average time-to-fix - so we bake in DM's assumption (GPS_FIX_S).

// Assumed GPS time-to-fix (seconds) for full-GPS devices, from Digital Matter's
// calculator defaults (cold ~40 s / warm ~10-20 s -> a ~30 s working assumption).
export const GPS_FIX_S = 30;

// Coefficients per device family. Costs in mAh unless noted. `gpsMode`:
//  - 'perSecond' full-GPS devices  -> gps cost = cGps * GPS_FIX_S
//  - 'perFix'    Edge cloud-solve  -> gps cost = cGps (flat, fix-time independent)
// `cWifi` = energy per Wi-Fi scan (Edge devices only; null = no Wi-Fi positioning).
export const BATTERY_FAMILIES = {
  oyster3:    { label: 'Oyster3',     capacity: 3200, gpsMode: 'perSecond', cGps: 0.00254, cWifi: null,   cUpload: 0.0499, cRecord: 0.00055, cBleScanS: null,    sleepMAhH: 0.01048,  selfPctYr: 1 },
  oysterEdge: { label: 'Oyster Edge', capacity: 3200, gpsMode: 'perFix',    cGps: 0.0210,  cWifi: 0.0115, cUpload: 0.0478, cRecord: 0.00122, cBleScanS: 0.00086, sleepMAhH: 0.01561,  selfPctYr: 3 },
  yabbyEdge:  { label: 'Yabby Edge',  capacity: 1200, gpsMode: 'perFix',    cGps: 0.0158,  cWifi: 0.0125, cUpload: 0.0473, cRecord: 0.00124, cBleScanS: 0.00086, sleepMAhH: 0.004275, selfPctYr: 3, bleEstimated: true },
  barraGps:   { label: 'Barra GPS',   capacity: 3200, gpsMode: 'perSecond', cGps: 0.00506, cWifi: null,   cUpload: 0.0586, cRecord: 0.00143, cBleScanS: null,    sleepMAhH: 0.015,    selfPctYr: 6, sleepEstimated: true },
  // No public calculator yet (launched 2026); its architecture resembles Oyster
  // Edge (GNSS + Wi-Fi, cloud-solved), so we borrow Edge coefficients and flag it.
  griffinAir: { label: 'GRIFFIN-AIR', capacity: 3200, gpsMode: 'perFix',    cGps: 0.0210,  cWifi: 0.0115, cUpload: 0.0478, cRecord: 0.00122, cBleScanS: 0.00086, sleepMAhH: 0.01561,  selfPctYr: 3, familyEstimated: true },
};
export const DEFAULT_FAMILY = 'oyster3';

// Does a family support Wi-Fi positioning? (Edge devices try Wi-Fi first.)
export const supportsWifi = (familyKey) => BATTERY_FAMILIES[familyKey]?.cWifi != null;

// Map a device's raw model string (subType / metadata.prodId) to a family key.
export function familyForModel(model) {
  const s = String(model || '').toLowerCase();
  if (s.includes('griffin')) return 'griffinAir';
  if (s.includes('yabby')) return 'yabbyEdge';
  if (s.includes('edge')) return 'oysterEdge';
  if (s.includes('barra')) return 'barraGps';
  if (s.includes('oyster')) return 'oyster3';
  // Digital Matter product ids seen in the fleet.
  if (s === '118') return 'griffinAir';
  if (s === '92') return 'oysterEdge';
  if (s === '110') return 'barraGps';
  return null; // unknown -> caller falls back to DEFAULT_FAMILY
}

export function defaultAssumptions(settings = {}) {
  return {
    tripsPerDay: 2 / 7,  // ~2 trips per week (UI shows this as trips/week)
    tripLenMin: 120,     // 2 h (UI shows this in hours)
    movingLogMin: Number(settings.reportIntervalMovingMin) || 5, // fix cadence while moving
    gpsFixS: GPS_FIX_S, // DM assumption, NOT a user input
    conditionsPct: 85,  // usable-capacity derate for coverage / cold; DM unquantified
    positioning: null,  // 'wifi' | 'gps'; null -> Wi-Fi first where supported, else GPS
  };
}

// Core model. `settings` = effective tracker settings (intervals, ble, ...).
// `assumptions` = the advanced inputs (trips, fix time, conditions, capacity).
// Returns a rich object for display; `years` is capped at 10 like DM's tables.
export function estimateBatteryLife(settings = {}, assumptions = {}, familyKey = DEFAULT_FAMILY) {
  const fam = BATTERY_FAMILIES[familyKey] || BATTERY_FAMILIES[DEFAULT_FAMILY];
  const a = { ...defaultAssumptions(settings), ...assumptions };
  const capacity = Number(a.capacity) || fam.capacity;

  const movingUploadMin = Math.max(0.25, Number(settings.reportIntervalMovingMin) || 5);
  const parkedMin       = Math.max(1, Number(settings.reportIntervalParkedMin) || 360);
  const heartbeatH      = Math.max(0.1, Number(settings.heartbeatHours) || 24);
  const movingLogMin    = Math.max(0.1, Number(a.movingLogMin) || movingUploadMin);
  const tripsPerDay     = Math.max(0, Number(a.tripsPerDay) || 0);
  const tripLenS        = Math.max(0, (Number(a.tripLenMin) || 0) * 60);

  // Cost of one position fix. GPS: per-fix (Edge) or per-second * assumed fix time
  // (full-GPS). Wi-Fi: a flat per-scan cost, much cheaper. Edge devices try Wi-Fi
  // first and fall back to GPS; we assume Wi-Fi succeeds in that mode.
  const wifiCapable = fam.cWifi != null;
  const positioning = wifiCapable ? (a.positioning || 'wifi') : 'gps';
  const gpsPerFix = fam.gpsMode === 'perSecond'
    ? fam.cGps * (Number(a.gpsFixS) || GPS_FIX_S)
    : fam.cGps;
  const perFix = (positioning === 'wifi' && wifiCapable) ? fam.cWifi : gpsPerFix;

  // Movement: DM's per-trip equation. The "2 +" is the start/stop overhead.
  const uploadsPerTrip = 2 + tripLenS / (movingUploadMin * 60);
  const fixesPerTrip   = 2 + tripLenS / (movingLogMin * 60);
  const movingPerDay = tripsPerDay * (uploadsPerTrip * fam.cUpload + fixesPerTrip * (fam.cRecord + perFix));

  // Idle/parked baseline: whichever is more frequent, parked reports or heartbeat.
  const idleUploadsPerDay = Math.max(1440 / parkedMin, 24 / heartbeatH);
  const idlePerDay = idleUploadsPerDay * (fam.cUpload + fam.cRecord + perFix);

  // BLE gateway scanning. Energy per scan = per-second cost * window seconds;
  // scans per day = 86400 / interval. cBleScanS is DM-official only for the Edge
  // families; for others we borrow the Edge value and flag it.
  let blePerDay = 0, bleCoeffEstimated = false;
  if (settings.bleScan) {
    const cBle = fam.cBleScanS ?? BATTERY_FAMILIES.oysterEdge.cBleScanS;
    bleCoeffEstimated = fam.cBleScanS == null || !!fam.bleEstimated;
    const interval = Math.max(1, Number(settings.bleScanIntervalS) || 60);
    const win = Math.max(1, Number(settings.bleScanWindowS) || 5);
    blePerDay = (86400 / interval) * (cBle * win);
  }

  const sleepPerDay = fam.sleepMAhH * 24;
  const selfDischargePerDay = (fam.selfPctYr / 100 * capacity) / 365;

  const activePerDay = movingPerDay + idlePerDay + blePerDay;
  const mAhPerDay = activePerDay + sleepPerDay + selfDischargePerDay;

  const usable = capacity * (Math.min(100, Math.max(1, Number(a.conditionsPct) || 85)) / 100);
  const days = mAhPerDay > 0 ? usable / mAhPerDay : Infinity;
  const years = days / 365;

  const notes = [];
  if (fam.familyEstimated) notes.push(`${fam.label} has no public DM calculator yet - figures borrowed from Oyster Edge.`);
  if (settings.bleScan && bleCoeffEstimated) notes.push('BLE scan energy for this family is estimated from Oyster Edge.');
  if (fam.sleepEstimated) notes.push('Sleep current for this family is an estimate.');

  return {
    familyKey, family: fam, capacity, usableCapacity: usable,
    mAhPerDay, days, years, cappedYears: Math.min(10, years),
    positioning, wifiCapable,
    breakdown: {
      moving: movingPerDay, idle: idlePerDay, ble: blePerDay,
      sleep: sleepPerDay, selfDischarge: selfDischargePerDay,
    },
    assumptions: a, notes,
  };
}

// Human label for a life estimate (localised number, capped like DM at 10y).
export function formatLife(est) {
  if (!isFinite(est.years)) return 'n/a';
  const y = est.years;
  if (y >= 10) return '10+ years';
  if (y >= 2) return `~${y.toFixed(1)} years`;
  if (y >= 1) return `~${y.toFixed(1)} year`;
  const months = y * 12;
  if (months >= 1) return `~${Math.round(months)} months`;
  return `~${Math.round(est.days)} days`;
}
